import { Query, Rdc, RdcStatus } from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import rdcStrings from 'features/rdc/rdcStrings.en';
import { CHECK_DEPOSIT_HISTORY } from 'features/rdc/screens/CheckDepositHistory/CheckDepositHistoryScreen.constants';
import { useEffect, useMemo, useRef, useState } from 'react';
import FetchCheckDepositHistory from 'services/graphql/queries/FetchCheckDepositHistory';
import { TransactionListItemProps } from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import { SectionedContentScreenSection } from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { formatCurrency } from 'shared/utils/string/currency';
import { formatDisplayDate } from 'shared/utils/string/date';
import { AnyVariables, CombinedError, useQuery } from 'urql';

export type LoadingState = null | 'refreshing' | 'loading' | 'fetchingPage';

interface CheckDepositScreenState {
  loadingState: LoadingState;
  response?: SectionedContentScreenSection[];
  error?: CombinedError;
  refresh?: (useInlineLoading: boolean) => void;
  loadMore?: () => void;
}

const submittedOn = (deposit: Rdc): Date => {
  if (deposit.submittedOn) {
    return new Date(deposit.submittedOn);
  }

  return new Date();
};

const groupDeposits = (deposits: Rdc[]): { [key: string]: Rdc[] } => {
  const pendingTitle = rdcStrings.checkHistoryTable.pendingSectionTitle;
  const depositsDictionary: { [key: string]: Rdc[] } = {};
  deposits.forEach((deposit) => {
    if (
      deposit.rdcStatus.status === RdcStatus.Settled ||
      deposit.rdcStatus.status === RdcStatus.Cancelled ||
      deposit.rdcStatus.status === RdcStatus.Rejected
    ) {
      const monthYear = submittedOn(deposit).toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      });
      if (!depositsDictionary[monthYear]) {
        depositsDictionary[monthYear] = [];
      }
      depositsDictionary[monthYear].push(deposit);
    } else if (
      deposit.rdcStatus.status === RdcStatus.Pending ||
      deposit.rdcStatus.status === RdcStatus.Approved ||
      deposit.rdcStatus.status === RdcStatus.New ||
      deposit.rdcStatus.status === RdcStatus.Confirmed ||
      deposit.rdcStatus.status === RdcStatus.UndefinedRdcStatus
    ) {
      if (!depositsDictionary[pendingTitle]) {
        depositsDictionary[pendingTitle] = [];
      }
      depositsDictionary[pendingTitle].push(deposit);
    }
  });
  return depositsDictionary;
};

const generateUISections = (
  groupDeposits: Record<string, Rdc[]>,
): SectionedContentScreenSection[] => {
  const pendingTitle = rdcStrings.checkHistoryTable.pendingSectionTitle;
  //handle pending section first
  const sectionList: SectionedContentScreenSection[] = [];
  const pendingSection: Rdc[] = groupDeposits[pendingTitle] || [];
  delete groupDeposits[pendingTitle];

  const pendingTransactionItems: VXLComponent[] = pendingSection.map(
    (pendingDeposit: Rdc) => {
      return generateTransactionItem(pendingDeposit);
    },
  );
  if (pendingTransactionItems.length !== 0) {
    sectionList.push({
      sectionHeader: {
        id: pendingTitle,
        name: VXLComponentId.SECTION_DIVIDER,
        props: {
          labelText: pendingTitle,
        },
      },
      data: pendingTransactionItems,
    });
  }

  //sort keys
  const sortedKeys = Object.keys(groupDeposits).sort((keyA, keyB) => {
    const dateA = new Date(keyA);
    const dateB = new Date(keyB);
    return dateB.getTime() - dateA.getTime();
  });

  //iterate through each sorted key and access dictionary this will give use the items in the correct order
  const dateSortedItems: SectionedContentScreenSection[] = sortedKeys.map(
    (timeKey) => {
      //sort for each month
      const sortedTransactions: Rdc[] = groupDeposits[timeKey].sort(
        (rdcA: Rdc, rdcB: Rdc) => {
          const dateA = submittedOn(rdcA);
          const dateB = submittedOn(rdcB);
          return dateB.getTime() - dateA.getTime();
        },
      );

      const sortedItems: VXLComponent[] = sortedTransactions.map((deposit) => {
        return generateTransactionItem(deposit);
      });
      return {
        sectionHeader: {
          id: timeKey,
          name: VXLComponentId.SECTION_DIVIDER,
          props: {
            labelText: timeKey,
          },
        },
        data: sortedItems,
      };
    },
  );

  return sectionList.concat(dateSortedItems);
};

const generateTransactionItem = (deposit: Rdc): VXLComponent => {
  const isPending =
    deposit.rdcStatus.status === RdcStatus.Pending ||
    deposit.rdcStatus.status === RdcStatus.Approved ||
    deposit.rdcStatus.status === RdcStatus.New ||
    deposit.rdcStatus.status === RdcStatus.Confirmed ||
    deposit.rdcStatus.status === RdcStatus.UndefinedRdcStatus;
  let defaultCheckTitle = rdcStrings.checkHistoryTable.defaultCheckTitle;
  if (
    deposit.confirmationNumber !== '' &&
    deposit.confirmationNumber !== null
  ) {
    defaultCheckTitle = `${rdcStrings.checkHistoryTable.confirmation} ${deposit.confirmationNumber}`;
  }

  const formattedDate = formatDisplayDate(submittedOn(deposit));
  const formattedAmount: string = formatCurrency(deposit.value.amount, true);
  const isCancelledOrRejected =
    deposit.rdcStatus.status === RdcStatus.Cancelled ||
    deposit.rdcStatus.status === RdcStatus.Rejected;
  const amountPrefix = isCancelledOrRejected ? '' : '+';
  const props: TransactionListItemProps = {
    isPending: isPending,
    titleText: defaultCheckTitle,
    secondaryText: `${formattedDate} • ${deposit.rdcStatus.copy}`,
    amountText: `${amountPrefix} ${formattedAmount}`,
    amountStyle: isCancelledOrRejected ? 'NEUTRAL' : 'POSITIVE',
    onPress: () => {
      if (deposit?.id) {
        router.push({
          pathname: '/move-money/check-deposit-history/[id]',
          params: {
            id: deposit.id,
          },
        });
      }
    },
  };

  return {
    id: deposit.id,
    name: VXLComponentId.TRANSACTION_LIST_ITEM,
    props,
  };
};

const formatCheckDepositHistoryData = (
  checkDepositHistoryData: Query,
): SectionedContentScreenSection[] => {
  const newCheckDepositsRDC = checkDepositHistoryData.rdcs.edges.map((item) => {
    return item.node;
  });
  const groupedDeposits = groupDeposits(newCheckDepositsRDC);
  return generateUISections(groupedDeposits);
};

export const useCheckDepositHistoryData = (): CheckDepositScreenState => {
  const refreshingRef = useRef(false);
  const fetchingMoreRef = useRef(false);
  const [items, setItems] = useState<SectionedContentScreenSection[]>([]);
  const [after, setAfter] = useState('');

  const [
    {
      data: checkDepositHistoryData,
      fetching: checkDepositHistoryFetching,
      error: checkDepositHistoryError,
    },
    refetchDepositCheckHistoryQuery,
  ] = useQuery<Query, AnyVariables>({
    query: FetchCheckDepositHistory,
    variables: { first: CHECK_DEPOSIT_HISTORY.pageSize, after },
    requestPolicy: 'cache-and-network',
  });

  const isRefreshing = refreshingRef.current;

  const loadMore = () => {
    if (
      fetchingMoreRef.current ||
      refreshingRef.current ||
      !checkDepositHistoryData?.rdcs.pageInfo.hasNextPage ||
      !checkDepositHistoryData?.rdcs.pageInfo.endCursor
    ) {
      return;
    }
    fetchingMoreRef.current = true;
    setAfter(checkDepositHistoryData?.rdcs.pageInfo.endCursor);
  };

  const loadingState = useMemo(() => {
    if (isRefreshing && checkDepositHistoryFetching) {
      return 'refreshing';
    } else if (checkDepositHistoryFetching) {
      if (fetchingMoreRef.current) {
        return 'fetchingPage';
      }
      return 'loading';
    }
    return null;
  }, [checkDepositHistoryFetching, isRefreshing]);

  const refresh = (useInlineLoading: boolean): void => {
    refreshingRef.current = useInlineLoading;
    if (!useInlineLoading) {
      setItems([]);
    }
    refetchDepositCheckHistoryQuery({ requestPolicy: 'network-only' });
  };

  useEffect(() => {
    if (checkDepositHistoryData) {
      if (fetchingMoreRef.current) {
        setItems((prevItems) => {
          const updatedItems = [...prevItems];
          const newItems = formatCheckDepositHistoryData(
            checkDepositHistoryData,
          );
          newItems.forEach((newSection) => {
            // this will add items to existing section if the pagination happens to cut up a section.
            // these new items will always be farther back in time so no need to reorder the data in the section
            const existingSectionIndex = updatedItems.findIndex(
              (section) =>
                section.sectionHeader?.id === newSection.sectionHeader?.id,
            );
            if (existingSectionIndex !== -1) {
              updatedItems[existingSectionIndex].data = [
                ...updatedItems[existingSectionIndex].data,
                ...newSection.data,
              ];
            } else {
              updatedItems.push(newSection);
            }
          });
          fetchingMoreRef.current = false;
          return updatedItems;
        });
      } else {
        refreshingRef.current = false;
        return setItems(formatCheckDepositHistoryData(checkDepositHistoryData));
      }
    }
  }, [checkDepositHistoryData]);

  return {
    loadingState,
    response: items,
    refresh,
    error: checkDepositHistoryError,
    loadMore,
  };
};
